import { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { exists } from 'utils/common';
import { useIsMobile } from 'utils/hooks';

export interface TabItemProps {
  title: string;
  onClick: (s: string) => void;
  isActive: boolean;
  className?: string;
  paramCount?: { title: string; count: number }[];
}

export interface TabNavProps {
  tabs: string[];
  onTabClick: (tabName: string) => any;
  selectedTabIndex?: number;
  selectedTabName?: string;
  className?: string;
  itemClassName?: string;
  paramCount?: { title: string; count: number }[];
}

function TabItem({ isActive, onClick, title, className, paramCount }: TabItemProps) {
  const isMobile = useIsMobile();
  const productCount = () => {
    if (paramCount) {
      return paramCount.map((c) => (c.title.includes(title) ? `(${c.count})` : ''));
    }
  };

  const tabItemRef = useRef<HTMLButtonElement>();

  useEffect(() => {
    if (isActive && isMobile) {
      tabItemRef.current?.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'end' });
    }
  }, [isActive, isMobile]);

  return (
    <button
      className={classNames(
        'flex flex-col whitespace-nowrap text-center justify-center',
        className,
        'text-md py-2 mr-[50px] rounded-0 font-semibold bg-transparent border-b-4',
        {
          'text-glooCharcoal border-primary-500': isActive,
          'text-glooMedGray border-transparent md:hover:border-glooGrayBorder hover:text-glooCharcoal': !isActive,
        },
      )}
      onClick={() => onClick(title)}
      data-durable-id="tabNavButton"
      data-intercom-target="tabNavButton"
      ref={tabItemRef}
    >
      {title} {productCount()}
    </button>
  );
}

const TabNav = forwardRef(
  ({ tabs, selectedTabIndex, selectedTabName, onTabClick, className, itemClassName, paramCount }: TabNavProps, ref) => {
    const [activeTab, setActiveTab] = useState(() =>
      selectedTabIndex && tabs[selectedTabIndex]
        ? tabs[selectedTabIndex]
        : selectedTabName && tabs.find((t) => t === selectedTabName)
          ? selectedTabName
          : tabs[0] || '',
    );

    const selectTab = (tabName: string) => {
      if (activeTab === tabName) return;

      setActiveTab(tabName);
      onTabClick(tabName);
    };

    useImperativeHandle(ref, () => ({
      setTab(tabName: string) {
        selectTab(tabName);
      },
    }));

    useEffect(() => {
      if (exists(selectedTabIndex) && tabs[selectedTabIndex] !== activeTab) {
        setActiveTab(tabs[selectedTabIndex]);
      }
    }, [selectedTabIndex]);

    useEffect(() => {
      if (exists(selectedTabName) && tabs.find((t) => t === selectedTabName) !== activeTab) {
        setActiveTab(selectedTabName);
      }
    }, [selectedTabName]);
    return (
      <div className={classNames('flex flex-row max-w-screen overflow-x-auto border-b border-primary', className)}>
        {tabs.map((tab, index) => (
          <TabItem
            key={index}
            title={tab}
            onClick={selectTab}
            isActive={tab === activeTab}
            paramCount={paramCount}
            className={itemClassName}
          />
        ))}
      </div>
    );
  },
);

TabNav.displayName = 'TabNav';

export default TabNav;
